import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';

const navTabs = [
  {
    label: 'Over ons',
    children: [
      { label: 'Over Staffing Force', path: '/over-ons' },
      { label: 'Historie', path: '/historie' },
      { label: 'Certificering', path: '/certificering' },
      { label: 'Blog & Nieuws', path: '/blog' },
    ],
  },
  {
    label: 'Diensten',
    children: [
      { label: 'Onze oplossingen', path: '/onze-oplossingen' },
      { label: 'Interim Management', path: '/diensten' },
      { label: 'Duurzame detachering', path: '/duurzame-detachering' },
      { label: 'Financeprofessionals', path: '/financeprofessionals' },
      { label: 'HR-professionals', path: '/hrprofessionals' },
      { label: 'Duurzaamheidsprofessionals', path: '/duurzaamheidsprofessionals' },
    ],
  },
  {
    label: 'Vacatures',
    children: [
      { label: 'Alle vacatures', path: '/vacatures' },
      { label: 'Voor kandidaten', path: '/voor-kandidaten' },
      { label: 'Voor opdrachtgevers', path: '/voor-opdrachtgevers' },
      { label: 'Opdracht opgeven', path: '/opdracht-opgeven' },
    ],
  },
  {
    label: 'Contact',
    children: [
      { label: 'Neem contact op', path: '/contact' },
      { label: 'Locaties in Nederland', path: '/locaties' },
      { label: 'Veelgestelde vragen', path: '/veelgestelde-vragen' },
      { label: 'Documentatie', path: '/documentatie' },
      { label: 'Recruitmentfraude', path: '/recruitmentfraude' },
    ],
  },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openTab, setOpenTab] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const [tabPositions, setTabPositions] = useState({});
  const location = useLocation();
  const tabRefs = useRef({});

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenTab(null);
  }, [location]);

  // Measure position of each tab to align dropdown items under it
  useEffect(() => {
    const measure = () => {
      const positions = {};
      Object.entries(tabRefs.current).forEach(([i, el]) => {
        if (el) {
          const rect = el.getBoundingClientRect();
          positions[i] = { left: rect.left, width: rect.width };
        }
      });
      setTabPositions(positions);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const isHome = location.pathname === '/';
  const transparentHero = isHome && !scrolled && openTab === null;
  const navBg = transparentHero ? 'bg-transparent' : 'bg-white shadow-sm';
  const textColor = transparentHero ? 'text-white' : 'text-gray-900';
  const logoInvert = transparentHero ? '' : 'brightness-0';

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${navBg}`}
        onMouseLeave={() => setOpenTab(null)}
        data-testid="main-navbar"
      >
        {/* Top rating bar - altijd zichtbaar */}
        <div className="bg-[#0d1b2a] text-white text-[11px] md:text-xs py-1.5 px-4 text-center font-medium tracking-wide">
          <span className="inline-flex items-center gap-2">
            <span className="text-[#00bfa5] tracking-tight">★★★★</span>
            Gemiddeld een 4.3 op LinkedIn en Google reviews
          </span>
        </div>
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center flex-shrink-0" data-testid="navbar-logo">
              <img
                src="https://customer-assets.emergentagent.com/job_staffforce-new-site/artifacts/8pffcrsv_image.png"
                alt="StaffingForce"
                className={`h-8 md:h-9 w-auto transition-all duration-300 ${logoInvert}`}
              />
            </Link>

            {/* Desktop Tabs — centered, spaced, uppercase, thin */}
            <div className="hidden md:flex items-center gap-10 lg:gap-14">
              {navTabs.map((tab, i) => (
                <button
                  key={tab.label}
                  ref={(el) => (tabRefs.current[i] = el)}
                  onMouseEnter={() => setOpenTab(i)}
                  onClick={() => setOpenTab(openTab === i ? null : i)}
                  className={`flex items-center gap-1.5 text-[13px] font-medium tracking-[0.12em] uppercase transition-colors hover:text-[#00897B] ${textColor}`}
                  data-testid={`nav-tab-${tab.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {tab.label}
                  <ChevronDown
                    size={14}
                    className={`transition-transform duration-200 ${openTab === i ? 'rotate-180' : ''}`}
                  />
                </button>
              ))}
            </div>

            {/* CTA */}
            <div className="hidden md:flex items-center gap-6 flex-shrink-0">
              <Link
                to="/contact"
                className="bg-[#00897B] hover:bg-[#00796B] text-white px-6 py-3 rounded-sm text-[13px] font-semibold tracking-wide transition-all duration-300 hover:shadow-lg hover:shadow-[#00897B]/25"
                data-testid="navbar-cta"
              >
                Neem contact op
              </Link>
            </div>

            {/* Mobile toggle */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`md:hidden p-2 transition-colors ${textColor}`}
              aria-label="Menu"
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Full-width mega dropdown panel (Team Eiffel style) */}
        <AnimatePresence>
          {openTab !== null && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="absolute top-full left-0 right-0 bg-white border-t border-gray-100 shadow-lg hidden md:block"
              onMouseEnter={() => setOpenTab(openTab)}
              data-testid={`dropdown-panel-${openTab}`}
            >
              <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-10 relative">
                <div
                  className="flex flex-col gap-5"
                  style={{
                    marginLeft: tabPositions[openTab]
                      ? `${tabPositions[openTab].left - 48}px`
                      : 0,
                  }}
                >
                  {navTabs[openTab].children.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setOpenTab(null)}
                      className="text-[15px] font-normal text-gray-800 hover:text-[#00897B] transition-colors w-fit"
                      data-testid={`dropdown-link-${item.path}`}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-white pt-24 px-6 overflow-y-auto pb-20 md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1">
              <Link
                to="/"
                className="block text-base font-medium text-gray-800 hover:text-[#00897B] transition-colors py-3 border-b border-gray-100 uppercase tracking-wider text-sm"
                onClick={() => setMobileOpen(false)}
              >
                Home
              </Link>

              {navTabs.map((tab, i) => (
                <div key={tab.label} className="border-b border-gray-100">
                  <button
                    onClick={() => setMobileExpanded(mobileExpanded === i ? null : i)}
                    className="flex items-center justify-between w-full py-3 text-sm font-medium text-gray-800 uppercase tracking-wider"
                  >
                    {tab.label}
                    <ChevronDown
                      size={18}
                      className={`text-gray-400 transition-transform duration-200 ${
                        mobileExpanded === i ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  <AnimatePresence>
                    {mobileExpanded === i && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="pl-4 pb-3 space-y-1">
                          {tab.children.map((item) => (
                            <Link
                              key={item.path}
                              to={item.path}
                              className="block py-2 text-sm text-gray-600 hover:text-[#00897B] transition-colors"
                              onClick={() => setMobileOpen(false)}
                            >
                              {item.label}
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}

              <Link
                to="/contact"
                className="bg-[#00897B] text-white px-6 py-3 rounded-sm text-center font-semibold mt-4 text-sm tracking-wide"
                onClick={() => setMobileOpen(false)}
              >
                Neem contact op
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
